// İSG uzmanı portalı — Uygunluk belgesi ekleme (Modal + RHF + Zod). **FE'deki ilk
// dosya-yükleme akışı.** İki adım:
//   1. §8 yükleme: dosya seç → `documentsApi.uploadDocument` (create → S3 PUT (XHR
//      ilerleme) → confirm) → confirmed `documentUuid`.
//   2. §13.2 link: `contractorsApi.linkContractorDocument(uuid, {type, documentUuid,
//      validFrom?, validUntil?, note?})`.
//
// Dosya FE'de önden doğrulanır (`validateDocumentFile` — boyut/tip). Dosya seçimi
// **FileList snapshot** kuralına uyar: `e.target.value=''`'den ÖNCE `Array.from`
// ([[feedback_file_input_filelist_snapshot]]). Yükleme ilerlemesi çubukla gösterilir;
// hata durumunda temiz i18n mesajı. Belge dosyası sunucudan geçmez (doğrudan S3).
//
// Tür seçici ve bitiş tarihi kuralı §27 kaydının `company` türlerinden (`documentLinkForm` +
// `DocumentTypeField`, GL #7): varsayılan tür yok; bitiş tarihi türün `expiryRule`'una göre.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  DOCUMENT_ACCEPT_ATTR,
  Field,
  Input,
  Modal,
  contractorsApi,
  documentsApi,
  isTypeFieldRejection,
  resolveApiErrorMessage,
  useDocumentTypes,
  validateDocumentFile,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileUp, Paperclip } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { contractorsKeys } from '../../lib/contractorsKeys';
import { DocumentTypeField } from '../documents/DocumentTypeField';
import { ValidUntilField } from '../documents/ValidUntilField';
import {
  type DocumentLinkFormValues,
  documentLinkSchema,
  useDocumentLinkType,
} from '../documents/documentLinkForm';

interface DocumentLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractorUuid: string;
  onSuccess?: () => void;
}

function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

export function DocumentLinkModal({
  open,
  onOpenChange,
  contractorUuid,
  onSuccess,
}: DocumentLinkModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const { types } = useDocumentTypes();
  const schema = useMemo(() => documentLinkSchema(t, types, 'company'), [t, types]);
  const form = useForm<DocumentLinkFormValues>({
    resolver: zodResolver(schema),
    // Varsayılan tür YOK (GL #7 Review Focus 2): "Belge türü seçin" + zorunlu seçim.
    defaultValues: { type: '', validFrom: '', validUntil: '', note: '' },
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;
  const docType = useDocumentLinkType('company', form);
  // Eşzamanlı çift Enter/tık: RHF doğrulaması async → senkron ref ikinci gönderimi keser.
  const inflightRef = useRef(false);

  function pickFile(files: File[]) {
    const picked = files[0];
    if (!picked) {
      return;
    }
    const invalid = validateDocumentFile(picked);
    if (invalid) {
      setFile(null);
      setFileError(t(invalid));
      return;
    }
    setFileError(null);
    setFile(picked);
  }

  const mutation = useMutation({
    mutationFn: async (values: DocumentLinkFormValues) => {
      if (!file) {
        throw new Error('NO_FILE');
      }
      setProgress(0);
      // 1) §8 — doğrudan S3'e yükle + confirm → documentUuid.
      const document = await documentsApi.uploadDocument(file, {
        onProgress: (percent) => setProgress(percent),
      });
      // 2) §13.2 — confirmed dosyayı taşerona link'le. Link KESİN reddedilirse (4xx, ör. tür reddi)
      // yüklenen §8 dosyası yetim kalmasın diye silinir; ağ hatası/5xx'te silinmez (bağ kaydedilmiş
      // olabilir) — `linkUploadedDocument`.
      return documentsApi.linkUploadedDocument(document.uuid, () =>
        contractorsApi.linkContractorDocument(contractorUuid, {
          type: values.type,
          documentUuid: document.uuid,
          validFrom: nullable(values.validFrom),
          validUntil: docType.validUntilToSend(values),
          note: nullable(values.note),
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractorsKeys.documents(contractorUuid) });
      // Uygunluk özeti belgelerden türetilir → taşeron detayını + listeyi tazele.
      queryClient.invalidateQueries({ queryKey: contractorsKeys.detail(contractorUuid) });
      queryClient.invalidateQueries({ queryKey: contractorsKeys.list() });
      resetAll();
      onSuccess?.();
      onOpenChange(false);
    },
    // Tür reddi (§27.6): kayıt tazelenir, seçim temizlenir, hata alanda — aynı gövde yeniden gitmez.
    onError: (error) => docType.handleTypeRejection(error),
    onSettled: () => {
      inflightRef.current = false;
      setProgress(null);
    },
  });

  function resetAll() {
    reset();
    setFile(null);
    setFileError(null);
    setProgress(null);
  }

  // ✕ / Esc / Vazgeç hepsi aynı sıfırlamadan geçer (yarım form ve bayat hata kalmasın). Yükleme
  // sürerken kapanmaz: geç gelen hata sıfırlanmış forma düşer, yarım yükleme yetim kalırdı.
  function handleOpenChange(next: boolean) {
    if (mutation.isPending && !next) {
      return;
    }
    if (!next) {
      mutation.reset();
      resetAll();
    }
    onOpenChange(next);
  }

  const uploading = mutation.isPending;
  const canSubmit = file !== null && !uploading;

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title={t('contractors.documents.add.title')}
      description={t('contractors.documents.add.description')}
      size="md"
      closeLabel={t('contractors.common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={() => handleOpenChange(false)} disabled={uploading}>
            {t('contractors.common.cancel')}
          </Button>
          <Button type="submit" form="document-link-form" loading={uploading} disabled={!canSubmit}>
            {uploading
              ? t('contractors.documents.add.uploading')
              : t('contractors.documents.add.submit')}
          </Button>
        </>
      }
    >
      <form
        id="document-link-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => {
          if (inflightRef.current || mutation.isPending) {
            return; // gönderim sürüyor
          }
          inflightRef.current = true;
          mutation.mutate(values);
        })}
        noValidate
      >
        {mutation.isError && !isTypeFieldRejection(mutation.error) ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        {/* Dosya seçimi — FileList snapshot (reset'ten ÖNCE Array.from) */}
        <Field
          htmlFor="document-file"
          label={t('contractors.documents.field.file')}
          error={fileError ?? undefined}
          hint={t('contractors.documents.field.fileHint')}
          required
        >
          <label
            htmlFor="document-file"
            className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border-strong bg-surface px-3 py-3 text-sm text-muted hover:bg-surface-muted"
          >
            {file ? (
              <>
                <Paperclip className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span className="truncate text-fg">{file.name}</span>
              </>
            ) : (
              <>
                <FileUp className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{t('contractors.documents.field.filePlaceholder')}</span>
              </>
            )}
            <input
              id="document-file"
              type="file"
              className="sr-only"
              accept={DOCUMENT_ACCEPT_ATTR}
              disabled={uploading}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []); // önce snapshot
                e.target.value = ''; // sonra reset (aynı dosya tekrar seçilebilsin)
                if (files.length) {
                  pickFile(files);
                }
              }}
            />
          </label>
        </Field>

        {/* Yükleme ilerlemesi — native <progress> (semantik) + görsel çubuk */}
        {progress !== null ? (
          <div className="flex flex-col gap-1" aria-live="polite">
            <progress
              className="sr-only"
              value={progress}
              max={100}
              aria-label={t('contractors.documents.add.uploading')}
            />
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-surface-muted"
              aria-hidden="true"
            >
              <div
                className="h-full bg-primary transition-[width] duration-150"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs text-muted">
              {t('contractors.documents.add.progress', { percent: progress })}
            </span>
          </div>
        ) : null}

        <DocumentTypeField
          id="document-type"
          label={t('contractors.documents.field.type')}
          entity="company"
          error={errors.type?.message}
          registration={docType.typeField}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field htmlFor="document-valid-from" label={t('contractors.documents.field.validFrom')}>
            <Input id="document-valid-from" type="date" {...register('validFrom')} />
          </Field>
          <ValidUntilField
            id="document-valid-until"
            label={t('contractors.documents.field.validUntil')}
            rule={docType}
            error={errors.validUntil?.message}
            registration={register('validUntil')}
          />
        </div>

        <Field htmlFor="document-note" label={t('contractors.documents.field.note')}>
          <Input id="document-note" {...register('note')} />
        </Field>
      </form>
    </Modal>
  );
}
